import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { loadStripe, Stripe, StripeElementsOptions } from '@stripe/stripe-js';
import {
  BanknotesIcon,
  ChatBubbleLeftRightIcon,
  PrinterIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../../components/LoadingSpinner';
import EntityDrawer from '../../components/ui/EntityDrawer';
import { useTheme } from '../../context/ThemeContext';
import { publicPaymentRequestAPI } from '../../services/api';
import {
  buildDepositRequestTitle,
  resolveDepositPercent,
} from '../../utils/billingPresentation';

type CheckoutState = 'idle' | 'ready' | 'processing' | 'succeeded' | 'failed';

const PRINT_PREVIEW_POPUP_ERROR =
  'Unable to open print preview. Please allow pop-ups and retry.';

interface PublicSummary {
  request_id: string;
  status: 'pending_payment' | 'paid' | 'canceled' | 'expired' | 'replaced';
  purpose: 'full_balance' | 'deposit' | 'installment' | 'balance' | 'other';
  payable: boolean;
  currency: string;
  request_total_cents: number;
  allocated_total_cents: number;
  expires_at: string | null;
  paid_at: string | null;
  terms_snapshot_json?: {
    collection?: { type?: string; percent?: number };
    invoices?: Array<{
      billing_terms_snapshot_json?: {
        deposit?: { type?: string; value?: number };
      } | null;
    }>;
  } | null;
  terms_snapshot_hash?: string | null;
  customer: {
    name: string | null;
    email: string | null;
    company_name: string | null;
  };
  invoice_allocations: Array<{
    invoice_id: string;
    invoice_number: string;
    amount_due_cents: number;
    allocation_amount_cents: number;
    invoice_remaining_due_cents: number;
    status: string | null;
    issued_at: string | null;
    due_date: string | null;
    description: string | null;
    memo: string | null;
    line_item_count: number;
    line_items: Array<{
      id: string;
      description: string;
      quantity: number;
      unit_amount: number;
      amount: number;
    }>;
  }>;
}

interface PublicBranding {
  app_name: string | null;
  logo_url: string | null;
  primary_color: string | null;
  secondary_color: string | null;
  favicon_url: string | null;
  support_email?: string | null;
  support_phone?: string | null;
}

type PublicInvoiceAllocation = PublicSummary['invoice_allocations'][number];

interface PublicIntent {
  payment_intent_id: string;
  client_secret: string;
  status: string;
  amount: number;
  currency: string;
  payment_method_types?: string[];
  publishable_key: string;
  stripe_account_id: string | null;
}

function formatCurrency(cents: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: (currency || 'USD').toUpperCase(),
  }).format(cents / 100);
}

function formatDate(value: string | null): string {
  if (!value) {
    return 'n/a';
  }

  return new Date(value).toLocaleDateString();
}

function formatDateTime(value: string | null): string {
  if (!value) {
    return 'n/a';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'n/a';
  }

  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function customerPaymentStatusLabel(
  status: PublicSummary['status'] | undefined
): string {
  switch (status) {
    case 'pending_payment':
      return 'Ready to pay';
    case 'paid':
      return 'Paid';
    case 'canceled':
      return 'Canceled';
    case 'expired':
      return 'Expired';
    case 'replaced':
      return 'Updated link sent';
    default:
      return 'Payment request';
  }
}

function getAllocationDisplayText(allocation: PublicInvoiceAllocation): string {
  return (
    allocation.description ||
    allocation.memo ||
    `${allocation.line_item_count} line item(s)`
  );
}

function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

function buildTelHref(phone: string | null | undefined): string | null {
  const trimmed = (phone || '').trim();
  if (!trimmed) {
    return null;
  }

  const digits = trimmed.replace(/[^\d+]/g, '');
  if (!digits) {
    return null;
  }

  if (digits.startsWith('+')) {
    return `tel:${digits}`;
  }

  const numeric = digits.replace(/\D/g, '');
  if (numeric.length === 10) {
    return `tel:+1${numeric}`;
  }

  return `tel:${numeric}`;
}

function formatPaymentMethodLabel(method: string): string {
  const normalized = method.trim().toLowerCase();
  if (!normalized) {
    return 'Payment method';
  }

  return normalized
    .split('_')
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');
}

function cleanDisplayName(value: string | null | undefined): string {
  return (value || '')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^[\s:;,\-|/\\]+/, '')
    .replace(/[\s:;,\-|/\\]+$/, '')
    .trim();
}

function PaymentRequestCheckoutForm({
  clientSecret,
  amountLabel,
  customerEmail,
  paymentMethodTypes,
  onProcessing,
  onSucceeded,
  onFailed,
}: {
  clientSecret: string;
  amountLabel: string;
  customerEmail: string | null;
  paymentMethodTypes: string[];
  onProcessing: () => void;
  onSucceeded: () => void;
  onFailed: (message: string) => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPaymentElementReady, setIsPaymentElementReady] = useState(false);

  useEffect(() => {
    setIsPaymentElementReady(false);
  }, [clientSecret]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements || !isPaymentElementReady) {
      onFailed('Payment form is still loading. Please try again in a moment.');
      return;
    }

    setIsSubmitting(true);
    onProcessing();

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          receipt_email: customerEmail || undefined,
          return_url: window.location.href,
        },
        redirect: 'if_required',
      });

      if (error) {
        onFailed(error.message || 'Payment failed. Please try again.');
        return;
      }

      if (
        paymentIntent?.status === 'succeeded' ||
        paymentIntent?.status === 'processing'
      ) {
        onSucceeded();
        return;
      }

      onFailed(
        `Payment returned unexpected status: ${
          paymentIntent?.status || 'unknown'
        }`
      );
    } catch (error) {
      onFailed(
        error instanceof Error
          ? error.message
          : 'Payment failed. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="mb-2 block text-sm font-medium text-on-surface">
          Payment method
        </label>
        <div className="rounded-card border border-outline/70 bg-surface-container-low p-3 shadow-ambient">
          <PaymentElement
            onReady={() => setIsPaymentElementReady(true)}
            options={{
              layout: 'tabs',
              defaultValues: {
                billingDetails: {
                  email: customerEmail || undefined,
                },
              },
            }}
          />
        </div>
      </div>

      {paymentMethodTypes.length > 0 && (
        <div className="rounded-card border border-outline/70 bg-surface-container-low p-3">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-on-surface-variant">
            Available methods for this request
          </p>
          <div className="flex flex-wrap gap-2">
            {paymentMethodTypes.map((method) => (
              <span
                key={method}
                className="inline-flex items-center rounded-pill border border-outline/70 bg-surface-container-lowest px-2.5 py-1 text-xs font-medium text-on-surface"
              >
                {formatPaymentMethodLabel(method)}
              </span>
            ))}
          </div>
        </div>
      )}

      <button
        type="submit"
        disabled={!stripe || !isPaymentElementReady || isSubmitting}
        className="btn-primary w-full"
      >
        {isSubmitting ? 'Processing...' : `Pay ${amountLabel}`}
      </button>

      <p className="text-center text-xs text-on-surface-variant">
        Secure checkout powered by Stripe with dynamic method support.
      </p>
    </form>
  );
}

function PaymentRequestPage() {
  const { token } = useParams<{ token: string }>();
  const { effectiveTheme } = useTheme();
  const [checkoutState, setCheckoutState] = useState<CheckoutState>('idle');
  const [checkoutError, setCheckoutError] = useState<string>('');
  const [intent, setIntent] = useState<PublicIntent | null>(null);
  const [stripePromise, setStripePromise] =
    useState<Promise<Stripe | null> | null>(null);
  const [selectedInvoice, setSelectedInvoice] =
    useState<PublicInvoiceAllocation | null>(null);
  const [isInvoiceDrawerOpen, setIsInvoiceDrawerOpen] = useState(false);
  const [isContactDrawerOpen, setIsContactDrawerOpen] = useState(false);
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactMessage, setContactMessage] = useState('');
  const [contactNotice, setContactNotice] = useState('');
  const [contactError, setContactError] = useState('');

  const summaryQuery = useQuery(
    ['public-payment-request-summary', token],
    () => publicPaymentRequestAPI.getSummary(token || ''),
    {
      enabled: Boolean(token),
      retry: false,
      select: (response) => response.data as PublicSummary,
    }
  );

  const statusQuery = useQuery(
    ['public-payment-request-status', token],
    () => publicPaymentRequestAPI.getStatus(token || ''),
    {
      enabled:
        Boolean(token) &&
        (checkoutState === 'processing' || checkoutState === 'succeeded'),
      retry: false,
      refetchInterval: checkoutState === 'processing' ? 2000 : false,
      select: (response) =>
        response.data as {
          request_status: PublicSummary['status'];
          payable: boolean;
          payment_intent_status: string | null;
        },
    }
  );

  const brandingQuery = useQuery(
    ['public-payment-request-branding', token],
    () => publicPaymentRequestAPI.getBranding(),
    {
      enabled: Boolean(token),
      retry: false,
      select: (response) => response.data as PublicBranding,
    }
  );

  const createIntentMutation = useMutation(
    () => publicPaymentRequestAPI.createPaymentIntent(token || ''),
    {
      onSuccess: async (response) => {
        const payload = response.data as PublicIntent;
        setIntent(payload);
        setStripePromise(
          loadStripe(
            payload.publishable_key,
            payload.stripe_account_id
              ? { stripeAccount: payload.stripe_account_id }
              : undefined
          )
        );
        setCheckoutError('');
        setCheckoutState('ready');
      },
      onError: (error: any) => {
        const message =
          error?.response?.data?.message ||
          error?.message ||
          'Unable to initialize payment. Please retry from this link.';
        setCheckoutError(message);
        setCheckoutState('failed');
      },
    }
  );

  const contactMutation = useMutation(
    () =>
      publicPaymentRequestAPI.sendSupportContact(token || '', {
        name: contactName,
        email: contactEmail,
        message: contactMessage,
        publicUrl:
          typeof window !== 'undefined' ? window.location.href : undefined,
      }),
    {
      onSuccess: (response) => {
        if (response.data?.sent === false) {
          const error =
            response.data?.emailDelivery?.error ||
            response.data?.email_delivery?.error ||
            'Unable to send your message right now.';
          setContactNotice('');
          setContactError(error);
          return;
        }

        setContactError('');
        setContactNotice(
          `Thanks. Your message has been sent to ${tenantDisplayName} support.`
        );
        setContactMessage('');
      },
      onError: (error: any) => {
        const message =
          error?.response?.data?.message ||
          error?.message ||
          'Unable to send your message right now.';
        setContactNotice('');
        setContactError(message);
      },
    }
  );

  useEffect(() => {
    if (!statusQuery.data) {
      return;
    }

    if (statusQuery.data.request_status === 'paid') {
      setCheckoutState('succeeded');
      void summaryQuery.refetch();
      return;
    }

    if (statusQuery.data.request_status !== 'pending_payment') {
      setCheckoutState('failed');
      void summaryQuery.refetch();
    }
  }, [statusQuery.data, summaryQuery]);

  const summary = summaryQuery.data;
  const branding = brandingQuery.data;

  useEffect(() => {
    if (!summary) {
      return;
    }

    setContactName(
      (previous) => previous || cleanDisplayName(summary.customer.name)
    );
    setContactEmail((previous) => previous || summary.customer.email || '');
  }, [summary]);

  const tenantDisplayName = useMemo(() => {
    const brandName = (branding?.app_name || '').trim();
    if (brandName) {
      return brandName;
    }

    const customerCompany = cleanDisplayName(summary?.customer.company_name);
    if (customerCompany) {
      return customerCompany;
    }

    return 'Tenant Billing';
  }, [branding?.app_name, summary?.customer.company_name]);

  const supportPhoneNumber = (branding?.support_phone || '').trim();
  const supportPhoneHref = buildTelHref(supportPhoneNumber);

  useEffect(() => {
    const firstAllocationText = summary?.invoice_allocations?.[0]
      ? getAllocationDisplayText(summary.invoice_allocations[0])
      : '';
    const quoteMatch = firstAllocationText.match(/\bQT-[A-Z0-9-]+\b/i);
    const titlePrefix = quoteMatch
      ? `Charter Deposit ${quoteMatch[0]}`
      : summary?.request_id
      ? `Payment Request ${summary.request_id.slice(0, 8)}`
      : 'Payment Request';
    document.title = `${titlePrefix} | ${tenantDisplayName}`;
  }, [summary, tenantDisplayName]);

  const openInvoiceDrawer = (allocation: PublicInvoiceAllocation) => {
    setSelectedInvoice(allocation);
    setIsInvoiceDrawerOpen(true);
  };

  const openPrintPreview = () => {
    if (!selectedInvoice || !summary) {
      return;
    }

    setCheckoutError((current) =>
      current === PRINT_PREVIEW_POPUP_ERROR ? '' : current
    );

    const previewWindow = window.open('', '_blank', 'width=920,height=760');
    if (!previewWindow) {
      setCheckoutError(PRINT_PREVIEW_POPUP_ERROR);
      return;
    }

    const invoiceTitle = escapeHtml(selectedInvoice.invoice_number);
    const tenantTitle = escapeHtml(tenantDisplayName);
    const issueDate = escapeHtml(formatDate(selectedInvoice.issued_at));
    const dueDate = escapeHtml(formatDate(selectedInvoice.due_date));
    const status = escapeHtml(selectedInvoice.status || 'n/a');
    const details = escapeHtml(
      selectedInvoice.description ||
        selectedInvoice.memo ||
        `${selectedInvoice.line_item_count} line item(s)`
    );
    const amount = escapeHtml(
      formatCurrency(selectedInvoice.amount_due_cents, summary.currency)
    );
    const invoiceId = escapeHtml(selectedInvoice.invoice_id);
    const lineItems =
      selectedInvoice.line_items && selectedInvoice.line_items.length > 0
        ? selectedInvoice.line_items
            .map((item) => {
              const description = escapeHtml(item.description || 'Line item');
              const quantity = escapeHtml(String(item.quantity));
              const unitAmount = escapeHtml(
                formatCurrency(item.unit_amount, summary.currency)
              );
              const itemAmount = escapeHtml(
                formatCurrency(item.amount, summary.currency)
              );

              return `<tr>
        <td>${description}<div class="muted">Qty ${quantity} x ${unitAmount}</div></td>
        <td class="right">${itemAmount}</td>
      </tr>`;
            })
            .join('')
        : '<tr><td colspan="2" class="muted">No line item details available.</td></tr>';

    try {
      previewWindow.opener = null;
      previewWindow.document.write(`<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${tenantTitle} - ${invoiceTitle}</title>
    <style>
      * { box-sizing: border-box; }
      body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; margin: 32px; color: #111827; }
      h1 { margin: 0 0 8px; font-size: 28px; }
      h2 { margin: 0 0 24px; font-size: 20px; font-weight: 600; color: #374151; }
      table { width: 100%; border-collapse: collapse; margin-top: 16px; }
      th, td { border-bottom: 1px solid #e5e7eb; padding: 12px 0; text-align: left; vertical-align: top; }
      th { color: #6b7280; font-size: 12px; letter-spacing: 0.06em; text-transform: uppercase; }
      .meta { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; margin-bottom: 20px; }
      .meta div, .details { border: 1px solid #e5e7eb; border-radius: 8px; padding: 12px; }
      .label, .muted { color: #6b7280; }
      .label { display: block; font-size: 12px; font-weight: 700; letter-spacing: 0.06em; margin-bottom: 4px; text-transform: uppercase; }
      .amount { margin-top: 18px; border-radius: 8px; background: #eef2ff; padding: 16px; font-size: 26px; font-weight: 700; }
      .details { margin-top: 14px; line-height: 1.5; white-space: pre-wrap; }
      .muted { margin-top: 4px; font-size: 12px; }
      .right { text-align: right; white-space: nowrap; }
      .invoice-id { margin-top: 18px; font-size: 12px; color: #6b7280; }
      @media print {
        body { margin: 24px; }
      }
    </style>
  </head>
  <body>
    <h1>${tenantTitle}</h1>
    <h2>Invoice ${invoiceTitle}</h2>
    <div class="meta">
      <div><span class="label">Status</span>${status}</div>
      <div><span class="label">Issued</span>${issueDate}</div>
      <div><span class="label">Due</span>${dueDate}</div>
    </div>
    <div class="details"><span class="label">Details</span>${details}</div>
    <table>
      <thead>
        <tr>
          <th>Line item</th>
          <th class="right">Amount</th>
        </tr>
      </thead>
      <tbody>
        ${lineItems}
      </tbody>
    </table>
    <div class="amount">Amount Due: ${amount}</div>
    <div class="invoice-id">Invoice ID: ${invoiceId}</div>
    <script>
      window.addEventListener('load', () => {
        window.focus();
        setTimeout(() => window.print(), 150);
      });
    </script>
  </body>
</html>`);
      previewWindow.document.close();
      previewWindow.focus();
    } catch {
      previewWindow.close();
      setCheckoutError(PRINT_PREVIEW_POPUP_ERROR);
    }
  };

  const handleContactSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setContactNotice('');
    setContactError('');
    contactMutation.mutate();
  };

  const totalLabel = useMemo(
    () =>
      formatCurrency(
        summary?.request_total_cents || 0,
        summary?.currency || 'USD'
      ),
    [summary?.request_total_cents, summary?.currency]
  );

  const remainingDueCents = Math.max(
    0,
    (summary?.request_total_cents || 0) - (summary?.allocated_total_cents || 0)
  );
  const remainingDueLabel = formatCurrency(
    remainingDueCents,
    summary?.currency || 'USD'
  );
  const isRequestPaid = summary?.status === 'paid';
  const paymentContext = useMemo(() => {
    const allocations = summary?.invoice_allocations || [];
    const combinedText = allocations
      .flatMap((allocation) => [
        allocation.invoice_number,
        allocation.description,
        allocation.memo,
        ...(allocation.line_items || []).map((item) => item.description),
      ])
      .filter(Boolean)
      .join(' ');
    const quoteMatch =
      combinedText.match(/\bQT-[A-Z0-9-]+\b/i) ||
      combinedText.match(/\bquote\s+([A-Z0-9-]+)/i);
    const quoteNumber = quoteMatch
      ? quoteMatch[0].toUpperCase().startsWith('QUOTE ')
        ? quoteMatch[1]
        : quoteMatch[0]
      : null;
    const isQuoteDeposit =
      /deposit\s+for\s+quote/i.test(combinedText) ||
      Boolean(quoteNumber && /deposit/i.test(combinedText));
    const isDeposit = summary?.purpose === 'deposit' || isQuoteDeposit;
    const depositPercent = resolveDepositPercent(summary?.terms_snapshot_json);
    const isSingleItem = allocations.length === 1;

    return {
      isQuoteDeposit,
      isDeposit,
      depositPercent,
      isSingleItem,
      quoteNumber,
    };
  }, [summary]);

  const isDarkTheme = effectiveTheme === 'dark';
  const isLargePayment = (summary?.request_total_cents || 0) >= 1_000_000;

  const elementsOptions = useMemo<StripeElementsOptions | undefined>(() => {
    if (!intent) {
      return undefined;
    }

    return {
      clientSecret: intent.client_secret,
      loader: 'auto',
      appearance: {
        theme: isDarkTheme ? 'night' : 'stripe',
        variables: {
          colorPrimary: branding?.primary_color || '#004ac6',
          colorBackground: isDarkTheme ? '#10161f' : '#ffffff',
          colorText: isDarkTheme ? '#f3f7ff' : '#131b2e',
          colorTextSecondary: isDarkTheme ? '#a2aec2' : '#49454f',
          colorIcon: isDarkTheme ? '#a2aec2' : '#49454f',
          colorDanger: isDarkTheme ? '#f87171' : '#ba1a1a',
          fontFamily:
            "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          borderRadius: '14px',
        },
        rules: isDarkTheme
          ? {
              '.Input': {
                backgroundColor: '#131923',
                border: '1px solid rgba(148, 163, 184, 0.2)',
              },
              '.Tab': {
                backgroundColor: '#131923',
                border: '1px solid rgba(148, 163, 184, 0.2)',
              },
              '.Tab--selected': {
                backgroundColor: '#202938',
                borderColor: 'rgba(96, 165, 250, 0.7)',
              },
              '.Label': {
                color: '#a2aec2',
              },
            }
          : undefined,
      },
    };
  }, [intent, branding?.primary_color, isDarkTheme]);

  if (summaryQuery.isLoading) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-16">
        <LoadingSpinner className="py-20" />
      </div>
    );
  }

  if (summaryQuery.isError || !summary) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-16">
        <div className="card p-8 text-center">
          <h1 className="text-2xl font-bold text-on-surface">
            Payment link unavailable
          </h1>
          <p className="mt-3 text-on-surface-variant">
            This payment link is invalid or no longer active.
          </p>
        </div>
      </div>
    );
  }

  const showCheckout =
    summary.payable &&
    intent &&
    stripePromise &&
    elementsOptions &&
    checkoutState !== 'processing' &&
    checkoutState !== 'succeeded';
  const primaryColor = branding?.primary_color || '#2563eb';
  const statusLabel = customerPaymentStatusLabel(summary.status);
  const pageLabel = paymentContext.isDeposit
    ? paymentContext.isQuoteDeposit
      ? 'Charter deposit'
      : 'Invoice deposit'
    : 'Secure payment';
  const pageTitle = paymentContext.isDeposit
    ? buildDepositRequestTitle(
        paymentContext.isQuoteDeposit,
        paymentContext.depositPercent
      )
    : `${tenantDisplayName} payment request`;
  const pageDescription = paymentContext.isQuoteDeposit
    ? `Complete the deposit for ${
        paymentContext.quoteNumber || 'your accepted quote'
      } to move your transportation into confirmation.`
    : paymentContext.isDeposit
    ? 'Review the deposit allocation below. This payment is applied to the remaining invoice balance.'
    : paymentContext.isSingleItem
    ? 'Review the payment details below and continue when you are ready.'
    : 'Review the open items below and complete them together in one secure payment.';
  const itemSectionTitle = paymentContext.isDeposit
    ? 'Deposit Details'
    : 'Payment Details';
  const totalDueLabel = paymentContext.isDeposit
    ? 'Deposit due today'
    : 'Total due';
  const primaryButtonLabel = paymentContext.isDeposit
    ? `Pay deposit ${totalLabel}`
    : `Continue to pay ${totalLabel}`;
  const customerName =
    cleanDisplayName(summary.customer.name) ||
    cleanDisplayName(summary.customer.company_name) ||
    'Customer';
  const customerCompanyName = cleanDisplayName(summary.customer.company_name);

  return (
    <main className="min-h-screen bg-surface pb-28 lg:pb-0">
      <header className="bg-[#101724] text-white">
        <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6">
          <div className="flex flex-wrap items-center justify-between gap-5">
            <div className="flex min-w-0 items-center gap-4">
              {branding?.logo_url && (
                <img
                  src={branding.logo_url}
                  alt={`${tenantDisplayName} logo`}
                  className="h-16 w-auto max-w-[15rem] rounded-md object-contain"
                />
              )}
              {!branding?.logo_url && (
                <div className="flex h-14 w-14 items-center justify-center rounded-md border border-white/20 bg-white/10 text-lg font-semibold">
                  {tenantDisplayName.slice(0, 1)}
                </div>
              )}
              <div className="min-w-0">
                <p className="text-sm text-white/70">{pageLabel}</p>
                <h1 className="text-xl font-semibold leading-tight sm:text-2xl">
                  {tenantDisplayName}
                </h1>
              </div>
            </div>
            <div className="text-left sm:text-right">
              <p className="text-sm text-white/70">
                {paymentContext.quoteNumber ||
                  `Request ${summary.request_id.slice(0, 8)}`}
              </p>
              <span className="mt-2 inline-flex rounded-pill border border-white/20 bg-white/10 px-3 py-1 text-sm font-medium">
                {statusLabel}
              </span>
            </div>
          </div>

          <div className="mt-8 grid gap-6 lg:grid-cols-[minmax(0,1fr)_300px]">
            <div>
              <p className="text-sm font-medium uppercase text-white/60">
                {paymentContext.isQuoteDeposit
                  ? 'Final confirmation step'
                  : paymentContext.isDeposit
                  ? 'Deposit payment'
                  : 'Secure customer payment'}
              </p>
              <h2 className="mt-2 text-3xl font-semibold">{pageTitle}</h2>
              <p className="mt-4 max-w-2xl text-base leading-7 text-white/75">
                {pageDescription}
              </p>
              <div className="mt-5 flex flex-wrap gap-2 text-sm text-white/75">
                <span className="rounded-pill border border-white/15 bg-white/10 px-3 py-1">
                  Prepared for {customerName}
                </span>
                {summary.expires_at ? (
                  <span className="rounded-pill border border-white/15 bg-white/10 px-3 py-1">
                    Expires {formatDateTime(summary.expires_at)}
                  </span>
                ) : null}
              </div>
            </div>
            <div className="rounded-card border border-white/15 bg-white/10 p-4">
              <p className="text-sm text-white/70">{totalDueLabel}</p>
              <p className="mt-1 text-3xl font-semibold">{totalLabel}</p>
              <p className="mt-3 text-sm text-white/70">
                {paymentContext.isQuoteDeposit
                  ? 'This deposit is applied to your reservation balance.'
                  : paymentContext.isDeposit
                  ? 'This deposit is applied to your invoice balance.'
                  : 'Processed through a protected Stripe session.'}
              </p>
            </div>
          </div>
        </div>
      </header>

      <div className="mx-auto grid max-w-6xl gap-6 px-4 py-8 sm:px-6 lg:grid-cols-[minmax(0,1fr)_340px]">
        <section className="space-y-6">
          <div className="rounded-card border border-outline/60 bg-surface-container-lowest p-5">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <p className="text-xs font-semibold uppercase text-on-surface-variant">
                  Customer
                </p>
                <p className="mt-1 text-base font-semibold text-on-surface">
                  {customerName}
                </p>
                <p className="text-sm text-on-surface-variant">
                  {summary.customer.email || 'No email available'}
                </p>
                {customerCompanyName &&
                  customerCompanyName !== customerName && (
                    <p className="text-sm text-on-surface-variant">
                      {customerCompanyName}
                    </p>
                  )}
              </div>
              <div className="rounded-card border border-outline/60 bg-surface-container-low px-3 py-2 text-left sm:text-right">
                <p className="text-xs font-semibold uppercase text-on-surface-variant">
                  Reference
                </p>
                <p className="mt-1 max-w-xs break-all text-sm font-semibold text-on-surface">
                  {paymentContext.quoteNumber || summary.request_id}
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-card border border-outline/60 bg-surface-container-lowest p-5">
            <div className="mb-5">
              <p className="text-xs font-semibold uppercase text-on-surface-variant">
                {itemSectionTitle}
              </p>
              <h3 className="mt-1 text-xl font-semibold text-on-surface">
                {paymentContext.isQuoteDeposit
                  ? 'Reservation deposit'
                  : paymentContext.isDeposit
                  ? 'Invoice deposit'
                  : 'Items included in this payment'}
              </h3>
              <p className="mt-2 text-sm leading-6 text-on-surface-variant">
                {paymentContext.isQuoteDeposit
                  ? 'This payment secures the accepted quote and is recorded against the reservation.'
                  : paymentContext.isDeposit
                  ? 'Each amount below is the deposit allocated to that invoice.'
                  : 'Each item below is included in the total due for this secure payment link.'}
              </p>
            </div>

            <div className="hidden overflow-hidden rounded-card border border-outline/70 md:block">
              <table className="min-w-full table-fixed">
                <thead className="bg-surface-container-low">
                  <tr>
                    <th className="w-[18%] px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-on-surface-variant">
                      Reference
                    </th>
                    <th className="w-[14%] px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-on-surface-variant">
                      Status
                    </th>
                    <th className="w-[14%] px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-on-surface-variant">
                      Due
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-on-surface-variant">
                      Details
                    </th>
                    <th className="w-[14%] px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-on-surface-variant">
                      View
                    </th>
                    <th className="w-[16%] px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-on-surface-variant">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {summary.invoice_allocations.map((allocation) => (
                    <tr
                      key={allocation.invoice_id}
                      className="border-t border-outline/40"
                    >
                      <td className="px-3 py-3 text-sm font-medium text-on-surface">
                        {allocation.invoice_number}
                      </td>
                      <td className="px-3 py-2 text-sm text-on-surface-variant">
                        {allocation.status || 'n/a'}
                      </td>
                      <td className="px-3 py-2 text-sm text-on-surface-variant">
                        {formatDate(allocation.due_date)}
                      </td>
                      <td className="px-3 py-2 text-sm text-on-surface-variant">
                        {getAllocationDisplayText(allocation)}
                      </td>
                      <td className="px-3 py-2 text-sm">
                        <button
                          type="button"
                          onClick={() => openInvoiceDrawer(allocation)}
                          className="font-medium text-primary hover:text-primary-dark"
                        >
                          View details
                        </button>
                      </td>
                      <td className="px-3 py-2 text-right text-sm font-medium text-on-surface">
                        {formatCurrency(
                          allocation.amount_due_cents,
                          summary.currency
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="space-y-3 md:hidden">
              {summary.invoice_allocations.map((allocation) => (
                <button
                  key={allocation.invoice_id}
                  type="button"
                  onClick={() => openInvoiceDrawer(allocation)}
                  className="w-full rounded-card border border-outline/70 bg-surface-container-low p-4 text-left"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-on-surface">
                        {allocation.invoice_number}
                      </p>
                      <p className="mt-1 text-xs text-on-surface-variant">
                        Status: {allocation.status || 'n/a'}
                      </p>
                    </div>
                    <p className="text-sm font-semibold text-on-surface">
                      {formatCurrency(
                        allocation.amount_due_cents,
                        summary.currency
                      )}
                    </p>
                  </div>
                  <p className="mt-2 text-xs text-on-surface-variant">
                    {getAllocationDisplayText(allocation)}
                  </p>
                  <p className="mt-2 text-xs text-on-surface-variant">
                    Due {formatDate(allocation.due_date)}
                  </p>
                </button>
              ))}
            </div>
          </div>
        </section>

        <aside className="space-y-4 lg:sticky lg:top-6 lg:self-start">
          <div className="rounded-card border border-outline/60 bg-surface-container-lowest p-5 shadow-ambient">
            <div className="mb-3 flex items-center justify-between">
              <span className="text-sm font-medium text-on-surface-variant">
                {totalDueLabel}
              </span>
              <span className="text-2xl font-bold text-on-surface">
                {totalLabel}
              </span>
            </div>
            {paymentContext.isQuoteDeposit ? (
              <p className="mb-2 text-sm text-on-surface-variant">
                Applied to {paymentContext.quoteNumber || 'your accepted quote'}{' '}
                after payment.
              </p>
            ) : (
              <div className="mb-2 flex items-center justify-between">
                <span className="text-sm font-medium text-on-surface-variant">
                  Remaining due
                </span>
                <span className="text-sm font-semibold text-on-surface">
                  {remainingDueLabel}
                </span>
              </div>
            )}
            {isRequestPaid && summary.paid_at && (
              <p className="mb-2 text-xs font-medium text-success">
                Paid on {formatDateTime(summary.paid_at)}
              </p>
            )}
            {summary.expires_at && (
              <p className="text-xs text-on-surface-variant">
                Expires {formatDateTime(summary.expires_at)}
              </p>
            )}
          </div>

          {isLargePayment && (
            <div
              className={`rounded-card p-4 ${
                isDarkTheme
                  ? 'border border-sky-400/30 bg-sky-500/10'
                  : 'border border-info/30 bg-info-container/20'
              }`}
            >
              <div className="flex items-start gap-3">
                <BanknotesIcon
                  className={`mt-0.5 h-5 w-5 ${
                    isDarkTheme ? 'text-sky-300' : 'text-info'
                  }`}
                />
                <div>
                  <p
                    className={`text-sm font-semibold ${
                      isDarkTheme ? 'text-sky-100' : 'text-on-info-container'
                    }`}
                  >
                    Large payment support enabled
                  </p>
                  <p
                    className={`mt-1 text-xs ${
                      isDarkTheme ? 'text-sky-100/85' : 'text-on-info-container'
                    }`}
                  >
                    This request is above $10,000. Stripe will show eligible
                    payment methods such as bank-based options when your account
                    supports them.
                  </p>
                </div>
              </div>
            </div>
          )}

          {!summary.payable && !isRequestPaid && (
            <div className="rounded-card border border-outline/70 bg-surface-container-low p-4">
              <p className="text-sm font-medium text-on-surface">
                This payment link is no longer payable.
              </p>
              <p className="mt-1 text-sm text-on-surface-variant">
                Current status:{' '}
                <span className="font-medium">{summary.status}</span>
              </p>
            </div>
          )}

          {isRequestPaid && (
            <div className="rounded-card border border-success/30 bg-success-container/20 p-4">
              <p className="text-sm font-semibold text-success-on-container">
                {paymentContext.isDeposit ? 'Deposit paid' : 'Paid in full'}
              </p>
              <p className="mt-1 text-xs text-success-on-container">
                {paymentContext.isQuoteDeposit
                  ? 'Your deposit has been received and the reservation team can continue confirmation.'
                  : 'This payment has been completed. Balances in this request are now settled.'}
              </p>
              {summary.paid_at && (
                <p className="mt-2 text-xs text-success-on-container">
                  Confirmed at {formatDateTime(summary.paid_at)}
                </p>
              )}
            </div>
          )}

          {summary.payable && !intent && (
            <button
              type="button"
              className="w-full rounded-card px-4 py-3 text-sm font-semibold text-white transition hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-60"
              style={{ backgroundColor: primaryColor }}
              onClick={() => createIntentMutation.mutate()}
              disabled={createIntentMutation.isLoading}
            >
              {createIntentMutation.isLoading
                ? 'Preparing secure checkout...'
                : primaryButtonLabel}
            </button>
          )}

          {showCheckout && intent && stripePromise && elementsOptions && (
            <div className="rounded-card border border-outline/70 bg-surface-container-lowest p-4 shadow-ambient">
              <Elements stripe={stripePromise} options={elementsOptions}>
                <PaymentRequestCheckoutForm
                  clientSecret={intent.client_secret}
                  amountLabel={totalLabel}
                  customerEmail={summary.customer.email}
                  paymentMethodTypes={intent.payment_method_types || []}
                  onProcessing={() => {
                    setCheckoutError('');
                  }}
                  onSucceeded={() => {
                    setCheckoutError('');
                    setCheckoutState('processing');
                    void statusQuery.refetch();
                  }}
                  onFailed={(message) => {
                    setCheckoutError(message);
                    setCheckoutState('failed');
                  }}
                />
              </Elements>
            </div>
          )}

          {summary.payable && checkoutState === 'processing' && (
            <div className="rounded-card border border-info/30 bg-info-container/20 p-4">
              <p className="text-sm font-semibold text-on-info-container">
                Payment submitted
              </p>
              <p className="mt-1 text-xs text-on-info-container">
                We are confirming your payment with Stripe and updating the
                reservation balance. This usually takes a few seconds.
              </p>
            </div>
          )}

          <div className="rounded-card border border-outline/70 bg-surface-container-low p-4">
            <div className="flex items-start gap-2">
              <ShieldCheckIcon className="mt-0.5 h-5 w-5 text-primary" />
              <div>
                <p className="text-sm font-semibold text-on-surface">
                  Protected payment session
                </p>
                <p className="mt-1 text-xs text-on-surface-variant">
                  Encryption, payment authentication, and compliance controls
                  are managed directly by Stripe.
                </p>
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={() => {
              setContactNotice('');
              setContactError('');
              setIsContactDrawerOpen(true);
            }}
            className="flex w-full items-center justify-center gap-2 rounded-card border border-outline/70 px-4 py-3 text-sm font-medium text-primary hover:bg-surface-container-low"
          >
            <ChatBubbleLeftRightIcon className="h-5 w-5" />
            Questions before paying?
          </button>
        </aside>

        {checkoutError && (
          <div className="lg:col-span-2">
            <div
              className={`rounded-card p-3 text-sm ${
                isDarkTheme
                  ? 'border border-rose-400/30 bg-rose-500/10 text-rose-100'
                  : 'border border-error/30 bg-error-container/20 text-error-on-container'
              }`}
            >
              {checkoutError}
            </div>
          </div>
        )}

        {checkoutState === 'succeeded' && (
          <div className="lg:col-span-2">
            <div
              className={`rounded-card p-4 ${
                isDarkTheme
                  ? 'border border-emerald-400/35 bg-emerald-500/10 text-emerald-100'
                  : 'border border-success/30 bg-success-container/20 text-success-on-container'
              }`}
            >
              <p className="font-semibold">Payment complete.</p>
              <p className="mt-1 text-sm">
                {paymentContext.isQuoteDeposit
                  ? 'Your deposit has been paid and this link is now closed.'
                  : 'This payment request has been paid and this link is now closed.'}
              </p>
            </div>
          </div>
        )}
      </div>

      {summary.payable && !intent ? (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-outline/70 bg-surface-container-lowest/95 px-4 py-3 shadow-float backdrop-blur lg:hidden">
          <div className="mx-auto flex max-w-lg items-center gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-xs text-on-surface-variant">{totalDueLabel}</p>
              <p className="font-semibold text-on-surface">{totalLabel}</p>
            </div>
            <button
              type="button"
              className="rounded-card px-4 py-3 text-sm font-semibold text-white transition hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-60"
              style={{ backgroundColor: primaryColor }}
              onClick={() => createIntentMutation.mutate()}
              disabled={createIntentMutation.isLoading}
            >
              {createIntentMutation.isLoading ? 'Preparing...' : 'Continue'}
            </button>
          </div>
        </div>
      ) : null}

      <EntityDrawer
        open={isInvoiceDrawerOpen && Boolean(selectedInvoice)}
        onClose={() => setIsInvoiceDrawerOpen(false)}
        title={
          selectedInvoice ? selectedInvoice.invoice_number : 'Payment details'
        }
        subtitle={tenantDisplayName}
        widthClassName="max-w-xl"
        footer={
          <>
            <button
              type="button"
              onClick={openPrintPreview}
              className="btn-secondary inline-flex items-center gap-2"
            >
              <PrinterIcon className="h-5 w-5" />
              Open print preview
            </button>
            <button
              type="button"
              onClick={() => setIsInvoiceDrawerOpen(false)}
              className="btn-primary"
            >
              Close
            </button>
          </>
        }
      >
        {selectedInvoice && (
          <div className="space-y-4 text-sm">
            <div className="rounded-card border border-outline/70 bg-surface-container-low p-4">
              <p className="text-xs font-medium uppercase tracking-wide text-on-surface-variant">
                Amount in this request
              </p>
              <p className="mt-1 text-2xl font-bold text-on-surface">
                {formatCurrency(
                  selectedInvoice.amount_due_cents,
                  summary.currency
                )}
              </p>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-card border border-outline/70 p-3">
                <p className="text-xs uppercase tracking-wide text-on-surface-variant">
                  Status
                </p>
                <p className="mt-1 font-semibold text-on-surface">
                  {selectedInvoice.status || 'n/a'}
                </p>
              </div>
              <div className="rounded-card border border-outline/70 p-3">
                <p className="text-xs uppercase tracking-wide text-on-surface-variant">
                  Line items
                </p>
                <p className="mt-1 font-semibold text-on-surface">
                  {selectedInvoice.line_item_count}
                </p>
              </div>
              <div className="rounded-card border border-outline/70 p-3">
                <p className="text-xs uppercase tracking-wide text-on-surface-variant">
                  Issued date
                </p>
                <p className="mt-1 font-semibold text-on-surface">
                  {formatDate(selectedInvoice.issued_at)}
                </p>
              </div>
              <div className="rounded-card border border-outline/70 p-3">
                <p className="text-xs uppercase tracking-wide text-on-surface-variant">
                  Due date
                </p>
                <p className="mt-1 font-semibold text-on-surface">
                  {formatDate(selectedInvoice.due_date)}
                </p>
              </div>
            </div>

            <div className="rounded-card border border-outline/70 p-3">
              <p className="text-xs uppercase tracking-wide text-on-surface-variant">
                Description
              </p>
              <p className="mt-1 whitespace-pre-wrap text-on-surface">
                {selectedInvoice.description ||
                  selectedInvoice.memo ||
                  'No additional details available.'}
              </p>
            </div>

            <div className="rounded-card border border-outline/70 p-3">
              <p className="text-xs uppercase tracking-wide text-on-surface-variant">
                Line items
              </p>
              {selectedInvoice.line_items &&
              selectedInvoice.line_items.length > 0 ? (
                <div className="mt-2 space-y-2">
                  {selectedInvoice.line_items.map((item) => (
                    <div
                      key={item.id}
                      className="grid grid-cols-[minmax(0,1fr)_auto] gap-3 rounded-card border border-outline/40 bg-surface-container-low px-3 py-2"
                    >
                      <div>
                        <p className="text-sm font-medium text-on-surface">
                          {item.description || 'Line item'}
                        </p>
                        <p className="text-xs text-on-surface-variant">
                          Qty {item.quantity} ×{' '}
                          {formatCurrency(item.unit_amount, summary.currency)}
                        </p>
                      </div>
                      <p className="text-sm font-semibold text-on-surface">
                        {formatCurrency(item.amount, summary.currency)}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="mt-1 text-on-surface">
                  No line item details available.
                </p>
              )}
            </div>

            <p className="text-xs text-on-surface-variant">
              Invoice ID: {selectedInvoice.invoice_id}
            </p>
          </div>
        )}
      </EntityDrawer>

      <EntityDrawer
        open={isContactDrawerOpen}
        onClose={() => setIsContactDrawerOpen(false)}
        title="Contact billing"
        subtitle={`${tenantDisplayName} support`}
        widthClassName="max-w-lg"
      >
        <form className="space-y-4" onSubmit={handleContactSubmit}>
          <div>
            <label
              className="mb-1 block text-xs font-semibold uppercase tracking-wide text-on-surface-variant"
              htmlFor="contact-name"
            >
              Your name
            </label>
            <input
              id="contact-name"
              type="text"
              value={contactName}
              onChange={(event) => setContactName(event.target.value)}
              className="w-full rounded-card border border-outline/70 bg-surface-container-low px-3 py-2 text-sm text-on-surface"
              placeholder="Jane Doe"
              required
            />
          </div>

          <div>
            <label
              className="mb-1 block text-xs font-semibold uppercase tracking-wide text-on-surface-variant"
              htmlFor="contact-email"
            >
              Email
            </label>
            <input
              id="contact-email"
              type="email"
              value={contactEmail}
              onChange={(event) => setContactEmail(event.target.value)}
              className="w-full rounded-card border border-outline/70 bg-surface-container-low px-3 py-2 text-sm text-on-surface"
              placeholder="you@example.com"
              required
            />
          </div>

          <div>
            <label
              className="mb-1 block text-xs font-semibold uppercase tracking-wide text-on-surface-variant"
              htmlFor="contact-message"
            >
              Message
            </label>
            <textarea
              id="contact-message"
              value={contactMessage}
              onChange={(event) => setContactMessage(event.target.value)}
              className="min-h-[120px] w-full rounded-card border border-outline/70 bg-surface-container-low px-3 py-2 text-sm text-on-surface"
              placeholder={
                paymentContext.isQuoteDeposit
                  ? 'Share your question about this deposit or reservation...'
                  : 'Share your question about this payment request...'
              }
              required
            />
          </div>

          <button
            type="submit"
            className="btn-primary w-full"
            disabled={contactMutation.isLoading}
          >
            {contactMutation.isLoading ? 'Sending...' : 'Send message'}
          </button>

          {contactNotice && (
            <p className="rounded-card border border-success/30 bg-success-container/20 p-3 text-sm text-success-on-container">
              {contactNotice}
            </p>
          )}

          {contactError && (
            <p className="rounded-card border border-error/30 bg-error-container/20 p-3 text-sm text-error-on-container">
              {contactError}
            </p>
          )}

          {supportPhoneNumber && supportPhoneHref && (
            <div className="rounded-card border border-outline/70 bg-surface-container-low p-4 text-sm text-on-surface-variant">
              <p className="font-semibold text-on-surface">Prefer to talk?</p>
              <p className="mt-1">
                Call {tenantDisplayName} support directly from this device.
              </p>
              <a
                href={supportPhoneHref}
                className="mt-3 inline-flex items-center rounded-card border border-outline/70 px-3 py-2 font-medium text-primary hover:bg-surface-container"
              >
                Call {supportPhoneNumber}
              </a>
            </div>
          )}
        </form>
      </EntityDrawer>
    </main>
  );
}

export default PaymentRequestPage;
